// Controle de tempo e histórias: selos, cronômetro e progresso.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Flag, Layers, Pause, Play, Timer } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { formatMinutes, type EpicProgress, type StoryProgress, type TaskStatus } from '@jynexus/shared';
import { api, errorMessage } from '../lib/api.ts';
import { clsx } from './ui.tsx';

export function useMyTimer() {
  return useQuery({ queryKey: ['timer'], queryFn: api.myTimer, refetchInterval: 60_000, staleTime: 15_000 });
}

/** Tempo decorrido desde startedAt, atualizado a cada segundo. */
export function useElapsed(startedAt?: string | null) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!startedAt) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [startedAt]);
  if (!startedAt) return '';
  const sec = Math.max(0, Math.floor((now - new Date(startedAt).getTime()) / 1000));
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  return h ? `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}` : `${m}:${String(s).padStart(2, '0')}`;
}

function useTimerActions(onError?: (msg: string) => void) {
  const qc = useQueryClient();
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['timer'] });
    qc.invalidateQueries({ queryKey: ['tasks'] });
    qc.invalidateQueries({ queryKey: ['task'] });
    qc.invalidateQueries({ queryKey: ['taskActivity'] });
    qc.invalidateQueries({ queryKey: ['dashboard'] });
  };
  const opts = { onSuccess: refresh, onError: (e: unknown) => onError?.(errorMessage(e)) };
  return {
    start: useMutation({ mutationFn: (key: string) => api.startTimer(key), ...opts }),
    stop: useMutation({ mutationFn: (note?: string) => api.stopTimer(note), ...opts }),
  };
}

/** Selo "gasto / estimado" com barra; vermelho quando passa da estimativa. */
export function TimeBadge({ spent, estimate, className }: { spent: number; estimate?: number | null; className?: string }) {
  if (!spent && !estimate) return null;
  const over = !!estimate && spent > estimate;
  const pct = estimate ? Math.min(100, (spent / estimate) * 100) : 0;
  return (
    <span className={clsx('inline-flex items-center gap-1.5 text-[11px] tabular-nums', over ? 'text-red-600' : 'text-slate-500', className)} title={estimate ? `${formatMinutes(spent)} gastos de ${formatMinutes(estimate)} estimados` : `${formatMinutes(spent)} gastos`}>
      <Timer className="size-3" aria-hidden />
      <span>
        {formatMinutes(spent)}
        {estimate ? <span className="text-slate-400"> / {formatMinutes(estimate)}</span> : null}
      </span>
      {estimate ? (
        <span className="h-1 w-10 overflow-hidden rounded-full bg-slate-200" aria-hidden>
          <span className={clsx('block h-1 rounded-full', over ? 'bg-red-500' : 'bg-navy-500')} style={{ width: `${over ? 100 : pct}%` }} />
        </span>
      ) : null}
    </span>
  );
}

/** Botão iniciar/parar do meu cronômetro nesta tarefa. Não dispara arraste nem navegação do card. */
export function TimerButton({ taskKey, disabled, onError, size = 'sm' }: { taskKey: string; disabled?: boolean; onError?: (msg: string) => void; size?: 'sm' | 'md' }) {
  const timer = useMyTimer();
  const { start, stop } = useTimerActions(onError);
  const mine = timer.data?.timer?.taskKey === taskKey;
  const elapsed = useElapsed(mine ? timer.data?.timer?.startedAt : null);
  const busy = start.isPending || stop.isPending;
  const stopEvents = {
    onPointerDown: (e: React.PointerEvent) => e.stopPropagation(),
    onKeyDown: (e: React.KeyboardEvent) => e.stopPropagation(),
  };
  if (mine) {
    return (
      <button
        type="button"
        {...stopEvents}
        onClick={(e) => (e.preventDefault(), e.stopPropagation(), stop.mutate(undefined))}
        disabled={busy}
        className={clsx('inline-flex items-center gap-1 rounded-full bg-accent-500 font-medium text-white tabular-nums shadow-sm hover:bg-accent-600', size === 'sm' ? 'h-6 px-2 text-[11px]' : 'h-8 px-3 text-xs')}
        title="Parar o cronômetro e apontar o tempo"
        aria-label={`Parar cronômetro de ${taskKey}`}
      >
        <span className="size-1.5 animate-pulse rounded-full bg-white" aria-hidden />
        {elapsed}
        <Pause className="size-3" aria-hidden />
      </button>
    );
  }
  if (disabled) return null;
  return (
    <button
      type="button"
      {...stopEvents}
      onClick={(e) => (e.preventDefault(), e.stopPropagation(), start.mutate(taskKey))}
      disabled={busy}
      className={clsx('inline-flex items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 hover:border-accent-500 hover:text-accent-600', size === 'sm' ? 'size-6' : 'h-8 gap-1.5 px-3 text-xs font-medium')}
      title={timer.data?.timer ? `Iniciar aqui (encerra o cronômetro de ${timer.data.timer.taskKey})` : 'Iniciar cronômetro'}
      aria-label={`Iniciar cronômetro em ${taskKey}`}
    >
      <Play className="size-3" aria-hidden />
      {size === 'md' && 'Iniciar cronômetro'}
    </button>
  );
}

/** Cronômetro ativo no cabeçalho: tarefa, tempo corrido e parar. */
export function ActiveTimerPill() {
  const timer = useMyTimer();
  const { stop } = useTimerActions();
  const t = timer.data?.timer;
  const elapsed = useElapsed(t?.startedAt);
  if (!t) return null;
  return (
    <div className="inline-flex h-9 items-center gap-2 rounded-md border border-accent-100 bg-accent-50 pr-1 pl-2.5 text-xs">
      <span className="size-2 animate-pulse rounded-full bg-accent-500" aria-hidden />
      <Link to={`/tasks/${t.taskKey}`} className="hidden max-w-48 truncate text-slate-700 hover:underline md:inline" title={t.taskTitle}>
        <span className="font-mono font-semibold text-navy-700">{t.taskKey}</span> {t.taskTitle}
      </Link>
      <Link to={`/tasks/${t.taskKey}`} className="font-mono font-semibold text-navy-700 md:hidden">
        {t.taskKey}
      </Link>
      <span className="font-semibold text-accent-700 tabular-nums">{elapsed}</span>
      <button onClick={() => stop.mutate(undefined)} disabled={stop.isPending} className="inline-flex h-7 items-center gap-1 rounded bg-accent-500 px-2 font-medium text-white hover:bg-accent-600" aria-label="Parar cronômetro">
        <Pause className="size-3" /> Parar
      </button>
    </div>
  );
}

export function StoryChip({ story, className }: { story: { key: string; title: string; status?: TaskStatus }; className?: string }) {
  return (
    <Link
      to={`/tasks/${story.key}`}
      onPointerDown={(e) => e.stopPropagation()}
      className={clsx('inline-flex max-w-full items-center gap-1 rounded bg-violet-50 px-1.5 py-px text-[11px] text-violet-700 hover:bg-violet-100', className)}
      title={`História ${story.key}: ${story.title}`}
    >
      <Layers className="size-3 shrink-0" aria-hidden />
      <span className="truncate">{story.title}</span>
    </Link>
  );
}

export function StoryProgressBar({ progress, className }: { progress: StoryProgress; className?: string }) {
  return (
    <div className={className}>
      <div className="mb-1 flex items-center justify-between text-[11px] text-slate-500">
        <span>
          {progress.done}/{progress.total} tarefas{progress.canceled ? ` · ${progress.canceled} canceladas` : ''}
        </span>
        <span className="font-semibold text-slate-700">{progress.percentDone}%</span>
      </div>
      <div className="h-1.5 rounded-full bg-slate-200" role="progressbar" aria-valuenow={progress.percentDone} aria-valuemin={0} aria-valuemax={100} aria-label="Progresso da história">
        <div className="h-1.5 rounded-full bg-violet-500" style={{ width: `${progress.percentDone}%` }} />
      </div>
    </div>
  );
}

export function EpicChip({ epic, className }: { epic: { key: string; title: string }; className?: string }) {
  return (
    <Link
      to={`/tasks/${epic.key}`}
      onPointerDown={(e) => e.stopPropagation()}
      className={clsx('inline-flex max-w-full items-center gap-1 rounded bg-amber-50 px-1.5 py-px text-[11px] text-amber-700 hover:bg-amber-100', className)}
      title={`Épico ${epic.key}: ${epic.title}`}
    >
      <Flag className="size-3 shrink-0" aria-hidden />
      <span className="truncate">{epic.title}</span>
    </Link>
  );
}

export function EpicProgressBar({ progress, className }: { progress: EpicProgress; className?: string }) {
  return (
    <div className={className}>
      <div className="mb-1 flex items-center justify-between text-[11px] text-slate-500">
        <span>
          {progress.storiesDone}/{progress.stories} histórias · {progress.tasksDone}/{progress.tasks} tarefas
        </span>
        <span className="font-semibold text-slate-700">{progress.percentDone}%</span>
      </div>
      <div className="h-1.5 rounded-full bg-slate-200" role="progressbar" aria-valuenow={progress.percentDone} aria-valuemin={0} aria-valuemax={100} aria-label="Progresso do épico">
        <div className="h-1.5 rounded-full bg-amber-500" style={{ width: `${progress.percentDone}%` }} />
      </div>
    </div>
  );
}
